/**
 * Phase 6 · the deal screens' strings — the `board` part: the pipeline kanban (D4), the forecast
 * and the resource demand (D7). Spread into `HU` and `EN` in `../catalogue.ts`; a key in one and
 * not the other is a type error there.
 */
export const HU = {
  /* ── the pipeline (D4) ── */
  'dealBoard.column': '{name} oszlop',
  'dealBoard.column.weighted': 'súlyozva {value}',
  'dealBoard.card.noClose': 'nincs dátum',
  'dealBoard.card.days': '{count} nap a státuszban',
  'dealBoard.card.noNext': 'Nincs következő lépés',
  'dealBoard.card.indicative': 'ár',
  'dealBoard.card.indicativeHint': 'Indikatív ár — még nincs mögötte terv.',
  'dealBoard.card.plan': 'terv',
  'dealBoard.card.planHint': 'A terv tételeiből összeadva.',
  'dealBoard.moveTo': 'Áthelyezés: {name}',
  'dealBoard.empty': 'Nincs deal ebben a státuszban. Húzz ide egyet.',
  'dealBoard.emptyReadOnly': 'Nincs deal ebben a státuszban.',
  'dealBoard.noStatuses':
    'Még nincs nyitott státusz. A Deal menedzsment beállításaiban vehetsz fel egyet.',
  'dealBoard.closed': 'Lezárt',
  'dealBoard.closedEmpty': 'Még nincs lezárt deal.',
  'dealBoard.toast.moved': 'Státusz: {name}',
  'dealBoard.toast.undo': 'Visszavonás',
  'dealBoard.toast.undoFailed': 'A visszavonás nem sikerült',

  /* ── the forecast (D7) ── */
  'dealForecast.tile.target': 'Cél · {quarter}',
  'dealForecast.tile.pipeline': 'Nyitott pipeline',
  'dealForecast.tile.coverage': 'Fedezet',
  'dealForecast.tile.committed': 'Bekötött (súlyozott)',
  'dealForecast.note.low':
    'A fedezet az iparági hüvelykujj szerint 3–4× — a mostani szint alatta van, több pipeline kell.',
  'dealForecast.note.ok':
    'A fedezet az iparági hüvelykujj szerint 3–4× — a mostani szint rendben van.',
  'dealForecast.note.noTarget':
    'Nincs negyedéves cél, így fedezet sem számolható. A célt a Deal menedzsment beállításaiban adhatod meg.',
  'dealForecast.figure': 'Fő szám',
  'dealForecast.figure.weighted': 'Súlyozott',
  'dealForecast.figure.raw': 'Nyers',
  'dealForecast.table': 'Forecast kategóriánként és hónaponként',
  'dealForecast.column.total': 'Összesen',
  'dealForecast.desc.commit': 'A vezető rátette a nevét — a negyedéves számban benne van.',
  'dealForecast.desc.best_case': 'Reális, de még nem vállalás.',
  'dealForecast.desc.pipeline': 'Él, de messze van a döntéstől.',
  'dealForecast.desc.omitted': 'A forecastból szándékosan kivéve.',
  'dealForecast.contracted': 'Szerződött bevétel',
  'dealForecast.contractedHint': 'A meglévő szerződésekből — ez a biztos rész.',
  'dealForecast.expected': 'Terv + várható',
  'dealForecast.expectedHint': 'Szerződött bevétel és a forecast, a kizárt dealek nélkül.',

  /* ── the resource demand (D7) ── */
  'dealDemand.raw': 'Nyers óraszám',
  'dealDemand.note':
    'A deal-igény nem számít a kihasználtságba — ez az előrejelzés, nem a terv. Ablak: {from} – {to}.',
  'dealDemand.table': 'Erőforrás-igény szerepenként és hetenként',
  'dealDemand.column.role': 'Szerep',
  'dealDemand.legend':
    'A felső szám a nyitott dealek placeholder-igénye, az alsó a szerep szabad kapacitása ugyanarra a hétre. Piros, ha az igény meghaladja a szabad kapacitást.',
  'dealDemand.cell': 'Igény: {demand} · szabad kapacitás: {free}',
  'dealDemand.cellShort': 'Igény: {demand} · szabad kapacitás: {free} · hiány: {gap}',
  'dealDemand.cellNone': 'Nincs deal-igény erre a hétre.',
  'dealDemand.empty.title': 'Nincs nyitott deal-igény',
  'dealDemand.empty.body':
    'Ha egy nyitott deal Erőforrás fülén szerepet és heti órákat tervezel, itt látszik, melyik szerepből mennyi kell.',
} as const;

export const EN: Record<keyof typeof HU, string> = {
  'dealBoard.column': '{name} column',
  'dealBoard.column.weighted': 'weighted {value}',
  'dealBoard.card.noClose': 'no date',
  'dealBoard.card.days': '{count} days in status',
  'dealBoard.card.noNext': 'No next step',
  'dealBoard.card.indicative': 'price',
  'dealBoard.card.indicativeHint': 'An indicative price — no plan behind it yet.',
  'dealBoard.card.plan': 'plan',
  'dealBoard.card.planHint': "Summed from the plan's line items.",
  'dealBoard.moveTo': 'Move to: {name}',
  'dealBoard.empty': 'No deals in this status. Drag one here.',
  'dealBoard.emptyReadOnly': 'No deals in this status.',
  'dealBoard.noStatuses':
    'There are no open statuses yet. Add one in the Deal management settings.',
  'dealBoard.closed': 'Closed',
  'dealBoard.closedEmpty': 'No closed deals yet.',
  'dealBoard.toast.moved': 'Status: {name}',
  'dealBoard.toast.undo': 'Undo',
  'dealBoard.toast.undoFailed': 'The undo did not go through',

  'dealForecast.tile.target': 'Target · {quarter}',
  'dealForecast.tile.pipeline': 'Open pipeline',
  'dealForecast.tile.coverage': 'Coverage',
  'dealForecast.tile.committed': 'Commit (weighted)',
  'dealForecast.note.low':
    'The rule of thumb for coverage is 3–4× — the current level is below it, so more pipeline is needed.',
  'dealForecast.note.ok': 'The rule of thumb for coverage is 3–4× — the current level is fine.',
  'dealForecast.note.noTarget':
    'There is no quarterly target, so there is no coverage either. Set the target in the Deal management settings.',
  'dealForecast.figure': 'Primary figure',
  'dealForecast.figure.weighted': 'Weighted',
  'dealForecast.figure.raw': 'Raw',
  'dealForecast.table': 'Forecast by category and month',
  'dealForecast.column.total': 'Total',
  'dealForecast.desc.commit': "The owner has put their name to it — it is in the quarter's number.",
  'dealForecast.desc.best_case': 'Realistic, but not a commitment yet.',
  'dealForecast.desc.pipeline': 'Alive, but far from a decision.',
  'dealForecast.desc.omitted': 'Deliberately left out of the forecast.',
  'dealForecast.contracted': 'Contracted revenue',
  'dealForecast.contractedHint': 'From existing contracts — the certain part.',
  'dealForecast.expected': 'Plan + expected',
  'dealForecast.expectedHint': 'Contracted revenue plus the forecast, without the omitted deals.',

  'dealDemand.raw': 'Raw hours',
  'dealDemand.note':
    'Deal demand does not count towards utilisation — it is a forecast, not the plan. Window: {from} – {to}.',
  'dealDemand.table': 'Resource demand by role and week',
  'dealDemand.column.role': 'Role',
  'dealDemand.legend':
    "The upper figure is the open deals' placeholder demand, the lower one the role's free capacity for the same week. Red when the demand exceeds the free capacity.",
  'dealDemand.cell': 'Demand: {demand} · free capacity: {free}',
  'dealDemand.cellShort': 'Demand: {demand} · free capacity: {free} · shortfall: {gap}',
  'dealDemand.cellNone': 'No deal demand for this week.',
  'dealDemand.empty.title': 'No open deal demand',
  'dealDemand.empty.body':
    "Plan roles and weekly hours on an open deal's Resources tab and this shows how much of each role is needed.",
};
